import { FormikProvider, useFormik } from 'formik';
import Link from 'next/link';
import { useState } from 'react';
import 'react-phone-number-input/style.css';
import { useKeyPressEvent } from 'react-use';
import { Button } from 'src/shared-components';
import { InputField } from 'src/shared-components/Form';
import * as Yup from 'yup';

const FounderForm = ({
  onFormSubmitted,
  title,
  subtitle,
}: {
  onFormSubmitted: (values: any) => void;
  title?: string;
  subtitle?: string;
}) => {
  const [userAgrees, setUserAgrees] = useState(false);
  const form = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordRepeat: '',
      companyName: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First Name is required').label('First name'),
      lastName: Yup.string().required('Last Name is required').label('Last name'),
      email: Yup.string().email('Your Email must be valid').required('Your Email is required'),
      password: Yup.string()
        .min(8, 'Your Password must be longer than 8 characters')
        .required('A Password is required')
        .label('Password'),
      passwordRepeat: Yup.string()
        .required('Please Repeat your Password')
        .label('Repeat password')
        .oneOf([Yup.ref('password'), null], 'Your Passwords must match'),
      companyName: Yup.string().required("We need your companies name, use TBD if you don't have one."),
      phoneNumber: Yup.string().required('Your Phone Number is required'),
    }),
    onSubmit: values => {
      onFormSubmitted(values);
    },
  });

  const handleFormSubmit = () => {
    form.submitForm();
  };

  useKeyPressEvent('Enter', handleFormSubmit);

  return (
    <div className="pb-10 md:py-10 px-[18px] md:bg-white md:rounded-6xl md:px-10 md:max-w-[640px] mx-auto w-full">
      <div className="hidden md:block font-normal text-2xl md:text-4xl">
        {title ? (
          <>
            <h1 className="opacity-40 text-2xl">{subtitle}</h1>
            <h1 className="text-3xl">{title}</h1>
          </>
        ) : (
          <>
            <h1 className="">Sign up and let&apos;s find you a team.</h1>
            <h1 className="opacity-40">Let&apos;s build something together.</h1>
          </>
        )}
      </div>
      <FormikProvider value={form}>
        <div className="grid grid-cols-6 gap-4 mt-6">
          <div className="col-span-6">
            <InputField
              type="email"
              name="email"
              autoComplete="off"
              label="Email address"
              placeholder="Email address"
              required
              border={false}
            />
          </div>
          <div className="col-span-6 md:col-span-6 lg:col-span-3">
            <InputField
              type="password"
              name="password"
              autoComplete="off"
              label="Password"
              placeholder="Password"
              required
              border={false}
            />
          </div>
          <div className="col-span-6 md:col-span-6 lg:col-span-3">
            <InputField
              type="password"
              name="passwordRepeat"
              autoComplete="off"
              label="Confirm password"
              placeholder="Confirm password"
              required
              border={false}
            />
          </div>
          <div className="col-span-6 md:col-span-6 lg:col-span-3">
            <InputField
              type="text"
              name="firstName"
              autoComplete="off"
              label="First Name"
              placeholder="First name"
              required
              border={false}
            />
          </div>
          <div className="col-span-6 md:col-span-6 lg:col-span-3">
            <InputField
              type="text"
              name="lastName"
              autoComplete="off"
              label="Last Name"
              placeholder="Last name"
              required
              border={false}
            />
          </div>
          <div className="col-span-6">
            <InputField
              type="text"
              name="companyName"
              autoComplete="off"
              label="Company Name"
              placeholder="Company Name"
              required
              border={false}
            />
          </div>
          <div className="col-span-6">
            <InputField
              type="text"
              name="phoneNumber"
              autoComplete="off"
              label="Phone Number"
              placeholder="ex. 555-555-5555"
              required
              border={false}
            />
            <span className="text-sm text-neutral-400">For Huddle team use only. We won&apos;t share this information.</span>
          </div>
          <div className="col-span-6 mt-2 text-sm">
            <input
              className="mr-2 appearance-none rounded bg-checkBoxUncheckedGray border-checkBoxUncheckedStrike checked:border-none checked:bg-navy focus:bg-navy checked:hover:bg-navy h-5 w-5"
              type="checkbox"
              checked={userAgrees}
              onChange={() => setUserAgrees(!userAgrees)}
            />
            Yes, I understand and agree to the{' '}
            <Link href="https://huddle.works/terms" passHref>
              <a target="_blank" className="font-medium">
                Terms
              </a>
            </Link>{' '}
            and{' '}
            <Link href="https://huddle.works/privacy-policy" passHref>
              <a target="_blank" className="font-medium">
                Privacy Policy
              </a>
            </Link>
            <br />
          </div>
          <div className="col-span-6 mt-2">
            <Button
              data-testid="signUpButton"
              onClick={handleFormSubmit}
              size="xl"
              roundness="full"
              color="black"
              fullWidth
              disabled={!userAgrees}
            >
              Sign up
            </Button>
          </div>
        </div>
      </FormikProvider>
    </div>
  );
};

export default FounderForm;
